<template>
  <v-container fluid class="page--cases pos-relative d-flex">
    <v-container class="page-container">
      <v-row class="pt-8">
        <!-- header -->
        <v-col cols="12">
          <v-row>
            <v-col
              cols="6" order="1"
              md="3" order-md="1"
              class="d-flex align-end"
            >
              <router-link :to="{name: 'Cases'}" class="link text--secondary text-uppercase text-body-2">
                <fai class="mr-2" :icon="['fad', 'long-arrow-left']" />
                <span v-t="'page.cases.back_to_cases'" />
              </router-link>
              <v-spacer />
            </v-col>

            <!-- case info -->
            <v-col
              cols="12" order="3"
              md="6" order-md="2"
              class="d-flex justify-center align-end"
            >
              <div class="text-center align-center">
                <v-img
                  :src="caseImage"
                  :aspect-ratio="1"
                  contain
                  :min-width="64"
                  :max-width="64"
                  class="mx-auto mb-2"
                />
                <h4 v-if="gameCase" class="text-body-1 grey100--text text-truncate lh-1">
                  {{ gameCase.name }}
                </h4>
              </div>
            </v-col>

            <v-col
              cols="6" order="2"
              md="3" order-md="3"
              class="d-flex justify-end align-end"
            >
              <v-btn
                :ripple="false" depressed tile small
                color="grey400 text--secondary"
                :disabled="loading.open"
                @click="onDemoSpin"
              >
                <fai fixed-width :icon="['fad', 'play-circle']" class="blue300--text mr-1" />
                Demo spin
              </v-btn>
            </v-col>
          </v-row>
        </v-col>


        <!-- spinner -->
        <v-col cols="12" class="pb-0">
          <div class="spinner-container" :class="{'is-multi': amount > 1}">
            <CaseSpinner
              v-if="amount < 2"
              ref="spinner"
              :items="caseItems"
              :item-count="spinnerSettings.count"
              :item-count-buffer="10"
              :roll-duration="spinnerSettings.duration"
              :item-size="spinnerSettings.itemSize"
              :muted="muted"
              class="grey900"
            />
            <CaseSpinnerMulti
              v-else
              ref="spinner"
              :items="caseItems"
              :slots="amount"
              :roll-duration="spinnerSettings.duration"
              :item-count="spinnerSettings.count"
              :item-size="spinnerSettings.itemSize"
              :muted="muted"
              class="grey900"
            />
          </div>
        </v-col>

        <!-- amount input -->
        <v-col cols="12" md="6" class="d-flex justify-start">
          <v-btn
            color="green700 green200--text"
            class="mr-2 text-body-2 font-weight-semibold"
            large depressed tile :ripple="false"
            :disabled="!canOpen"
            @click="onOpen"
          >
            <span class="mr-2">Open {{ amount }} for</span>
            <scrap :amount="totalPrice" />
          </v-btn>

          <v-btn-toggle
            v-model="amount"
            mandatory borderless tile dense
            class="button-toggle"
            background-color="transparent"
          >
            <v-btn
              v-for="i in maxAmount" :key="i"
              :value="i" depressed small
              :ripple="false"
              active-class="primary800 primary200--text"
              :disabled="loading.open"
              min-width="32"
              class="mr-1"
            >
              <span class="font-weight-semibold text-body-2" v-text="i" />
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- settings -->
        <v-col cols="12" md="6" class="d-flex justify-end">
          <v-tooltip top content-class="rounded-0">
            <template #activator="{ on }">
              <v-btn
                v-model="fastSpin"
                :ripple="false" depressed tile
                class="bg-ui-panel-btn px-3 unwidth mr-2"
                active-class="blue800 blue100--text"
                v-on="on"
                @click="fastSpin = !fastSpin"
              >
                <fai fixed-width :icon="['fad', 'bolt']" />
              </v-btn>
            </template>
            <span>Fast spin</span>
          </v-tooltip>

          <v-tooltip top content-class="rounded-0">
            <template #activator="{ on }">
              <v-btn
                :ripple="false" depressed tile
                class="bg-ui-panel-btn px-3 unwidth mr-2"
                :class="muted ? 'primary500--text' : 'text--secondary'"
                v-on="on"
                @click="onToggleSounds(muted)"
              >
                <fai fixed-width :icon="['fas', muted ? 'volume-slash' : 'volume-up']" />
              </v-btn>
            </template>
            <span>Sounds</span>
          </v-tooltip>

          <v-tooltip top content-class="rounded-0">
            <template #activator="{ on }">
              <v-btn
                :ripple="false" depressed tile
                class="bg-ui-panel-btn text--secondary px-3 unwidth"
                v-on="on"
                @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'settings'})"
              >
                <fai fixed-width :icon="['fad', 'balance-scale']" />
              </v-btn>
            </template>
            <span>Change client seed</span>
          </v-tooltip>
        </v-col>

        <!-- last unboxings (this session) -->
        <!-- shows total unboxed -->
        <transition name="v-scale-sorta">
          <v-col v-if="session.spent" cols="12" class="my-unboxings py-6">
            <!-- {{ session }} -->

            <div class="d-flex align-end mb-2">
              <div>
                <h4 class="text-uppercase text-body-1 font-weight-bold primary500--text mb-1">
                  {{ session.items.length }} items unboxed
                </h4>
                <p class="text-body-2 mb-0">
                  These items are automatically sold and added to your balance.
                </p>
              </div>

              <v-spacer />

              <div class="d-flex text-uppercase text-caption">
                <!-- <div class="bg-ui-panel text--secondary px-2 py-2 mr-2">
                  <scrap :amount="session.spent" />
                </div> -->
                <div v-if="session.lastWon" class="font-weight-bold green800 green400--text px-3 py-2">
                  <span class="green100--text mr-1">Last spin</span>
                  <scrap :amount="session.lastWon" /> won
                </div>
              </div>
            </div>

            <transition-group name="t-session-item" class="my-items item-container pa-4 grey800">
              <CaseItem
                v-for="item in sessionItems" :key="item._id"
                :item="item"
                :image-size="72"
                is-scrap
                class="grey600"
              />
            </transition-group>
          </v-col>
        </transition>


        <v-col cols="12" class="mb-n4">
          <v-card
            color="grey400" class="pa-3"
            :to="{name: 'Crate Battles'}" :ripple="false"
            tile
          >
            <div class="d-flex align-center">
              <GameIcon game="caseBattles" width="48" class="flex-grow-0 ml-2 mr-4" />
              <div>
                <h4 class="text-body-1 font-weight-bold">
                  Unbox crates with your friends!
                </h4>
                <span class="text-body-2">
                  We now have Group Unboxing and Crate Battles! Click <b>here</b> to check it out.
                </span>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" class="mt-4">
          <h4 class="text-uppercase text-body-1 font-weight-bold mb-2 primary500--text">
            Items in this case
          </h4>
          <div class="item-container">
            <CaseItem
              v-for="item in caseItems" :key="item.id"
              :item="item"
              :image-size="72"
              is-scrap
              class="grey700"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CasesGame from '@/store/models/games/cases/CasesGame'
import CaseItem from '@/components/CaseItem'
import CaseSpinner from './components/CaseSpinner'
import CaseSpinnerMulti from './components/CaseSpinnerMulti'
import { pickItems } from './utils'

const defaultCaseImg = require('@/assets/img/games/cases/default-case.png')

export default {
  metaInfo() {
    return {
      title: this.gameCase?.name ?? 'Crate Unboxing',
    }
  },
  components: {
    CaseItem,
    CaseSpinner,
    CaseSpinnerMulti,
  },
  beforeRouteUpdate(to, from, next) {
    // reset amount
    this.setAmount(1)
    this.onEnterPage(to.params.slug)
    next()
  },
  data: () => ({
    loading: {
      case: true,
      open: false,
    },
    session: {
      spent: 0,
      items: [],
      lastWon: 0,
    },
  }),
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
      itemdb: 'items/items',
      isFastSpin: 'games/cases/fast',
      caseAmount: 'games/cases/amount',
      settings: 'settings/settings',
    }),
    fastSpin: {
      get() { return this.isFastSpin },
      set(val) { this.setFastSpin(val) },
    },
    amount: {
      get() { return this.caseAmount },
      set(val) { this.setAmount(val) },
    },
    maxAmount() {
      return this.conga?.games?.cases.maxAmount ?? 5
    },
    spinnerSettings() {
      const { fastSpin, amount } = this

      if (amount > 1) {
        return {
          count: fastSpin ? 10 : 40,
          duration: fastSpin ? 1500 : 6e3,
          itemSize: 120,
        }
      }

      return {
        count: fastSpin ? 10 : 60,
        duration: fastSpin ? 1500 : 7e3,
        itemSize: 120,
      }
    },
    slug() {
      return this.$route.params?.slug ?? null
    },
    gameCase() {
      return CasesGame.query().where('slug', this.slug).first()
    },
    caseImage() {
      return this.gameCase?.imageUrl || defaultCaseImg
    },
    totalWeight() {
      return this.gameCase?.items.reduce((acc, v) => acc + v.weight, 0)
    },
    caseItems() {
      const { gameCase } = this
      if (!gameCase) return []

      const { itemdb, totalWeight } = this

      const items = []

      for (const [i, caseItem] of gameCase.items.entries()) {
        const lastTicket = items[i - 1]?.ticketRange[1] ?? -1

        items.push({
          ...caseItem,
          ...itemdb[caseItem.id],
          odds: caseItem.weight / totalWeight,
          ticketRange: [lastTicket + 1, lastTicket + caseItem.weight],
        })
      }

      return items
    },
    //
    totalPrice() {
      return this.gameCase?.price * this.amount
    },
    canOpen() {
      if (this.loading.open) return false
      if (!this.user) return false
      if (this.totalPrice > this.user.balance) return false

      return true
    },
    sessionWon() {
      return this.session.items.reduce((acc, i) => acc + i.price, 0)
    },
    sessionItems() {
      const { itemdb } = this
      const items = [...this.session.items].reverse()

      // dont wreck pcs
      if (items.length > 30) {
        items.length = 30
      }

      return items.map(item => ({ ...item, ...itemdb[item.id] }))
    },
    //
    muted() {
      return !this.settings.games.cases.sounds
    },
  },
  created() {
    // reset amount
    this.setAmount(1)

    // case is already in memory
    if (this.caseItems?.length) return

    this.onEnterPage(this.slug)
  },
  methods: {
    ...mapActions({
      getCaseBySlug: 'games/cases/getBySlug',
      openCase: 'games/cases/open',
    }),
    ...mapMutations({
      setSettings: 'settings/setSettings',
      addOrRemoveBalance: 'user/addOrRemoveBalance',
      setFastSpin: 'games/cases/setFast',
      setAmount: 'games/cases/setAmount',
    }),
    async onEnterPage(slug) {
      try {
        const gameCase = await this.getCaseBySlug(slug)

        if (!gameCase) {
          this.$router.push({ name: '404' })
          return
        }

        this.loading.case = false
      } catch (err) {
        console.error(err)
      }
    },
    async onOpen() {
      this.loading.open = true
      try {
        const totalSpent = this.totalPrice

        const resp = await this.openCase({
          id: this.gameCase.id,
          price: this.gameCase.price,
        })

        if (this.gameCase.tags?.includes('valentines')) {
          // NOTE gleam
          const Gleam = window.Gleam || []
          Gleam.push(['valentines-case-9gUa7VwwdTJ8m', resp.betId])
        }

        this.$refs.spinner.reset()

        if (resp.totalWon > 0) {
          setTimeout(() => {
            this.addOrRemoveBalance(resp.totalWon)
          }, this.spinnerSettings.duration)
        }

        const { itemdb } = this
        if (resp.wonItems.length > 1) {
          await this.$refs.spinner.spin(
            resp.wonItems.map(i => ({ ...i, ...itemdb[i.id] })),
            resp.betId,
          )
        } else {
          const [wonItem] = resp.wonItems
          await this.$refs.spinner.spin(
            { ...wonItem, ...itemdb[wonItem.id] },
            resp.betId,
          )
        }

        this.session.spent += totalSpent
        this.session.lastWon = resp.totalWon
        this.session.items.push(...resp.wonItems.map((item, i) => ({
          ...item,
          _id: `${resp.betId}-${item.id}-${i}`,
        })))
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.open = false
      }
    },
    async onDemoSpin() {
      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      this.loading.open = true

      try {
        const wonItems = []
        const { amount, caseItems } = this

        for (let index = 0; index < amount; index++) {
          wonItems.push(pickItems(caseItems, Math.random()))
        }

        this.$refs.spinner.reset()

        const { itemdb } = this
        if (wonItems.length > 1) {
          await this.$refs.spinner.spin(
            wonItems.map(i => ({ ...i, ...itemdb[i.id] })),
            Math.random(),
          )
        } else {
          const [wonItem] = wonItems
          await this.$refs.spinner.spin(
            { ...wonItem, ...itemdb[wonItem.id] },
            Math.random(),
          )
        }
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.open = false
      }
    },
    //
    onToggleSounds(val) {
      this.settings.games.cases.sounds = val

      this.setSettings(this.settings)
    },
  },
}

</script>

<style lang="scss" scoped src="./case.scss"></style>
